<template>
  <div
    ref="reviewArea"
    class="relative col-span-5 h-fit"
    :class="{ 'pointer-events-none opacity-50': loadingReviews }"
    :style="customerReviewInlineStyle"
    data-testid="review-area"
  >
    <SfLoaderCircular
      v-if="loadingReviews"
      data-testid="loader"
      class="absolute top-[130px] right-0 left-0 m-auto z-raised"
      size="2xl"
    />

    <div
      v-if="hasTitle && props.content.layout.collapsible"
      id="customerReviewsAccordion"
      data-testid="reviews-accordion"
    >
      <UiAccordionItem
        v-model="reviewsOpen"
        summary-class="@md:rounded-md w-full hover:bg-neutral-100 py-2 flex justify-between items-center select-none"
        content-padding-class=""
      >
        <template #summary>
          <h2 id="customerReviewsClick" class="font-bold text-lg leading-6 @md:text-2xl">
            {{ props.content.text.title }}
          </h2>
        </template>

        <UiReviewStatistics :product="product" />

        <UiReview v-for="(reviewItem, key) in authenticatedProductReviews" :key="key" :review-item="reviewItem" />
        <UiReview v-for="(reviewItem, key) in paginatedProductReviews" :key="key" :review-item="reviewItem" />
        <p
          v-if="paginatedProductReviews.length === 0"
          data-testid="no-review-text"
          class="font-bold leading-6 w-full py-2"
        >
          {{ t('product.noReviews') }}
        </p>
        <UiPagination
          v-if="paginatedProductReviews.length > 0"
          :key="pagination.totalCount"
          :current-page="currentPage"
          :total-items="pagination.totalCount"
          :page-size="config.defaultItemsPerPage"
          :max-visible-pages="maxVisiblePages"
          current-page-name="feedbackPage"
        />
      </UiAccordionItem>
    </div>

    <div v-else>
      <h2 id="customerReviewsClick" class="font-bold text-lg leading-6 @md:text-2xl">
        {{ props.content.text.title }}
      </h2>

      <UiReviewStatistics :product="product" />

      <UiReview v-for="(reviewItem, key) in paginatedProductReviews" :key="key" :review-item="reviewItem" />
      <p
        v-if="paginatedProductReviews.length === 0"
        data-testid="no-review-text"
        class="font-bold leading-6 w-full py-2"
      >
        {{ t('product.noReviews') }}
      </p>
      <UiPagination
        v-if="paginatedProductReviews.length > 0"
        :key="pagination.totalCount"
        :current-page="currentPage"
        :total-items="pagination.totalCount"
        :page-size="config.defaultItemsPerPage"
        :max-visible-pages="maxVisiblePages"
        current-page-name="feedbackPage"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { productGetters, reviewGetters } from '@plentymarkets/shop-api';
import { SfLoaderCircular } from '@storefront-ui/vue';
import type { ProductAccordionPropsType } from '~/components/ReviewsAccordion/types';
import type { CustomerReviewProps } from './types';

const props = defineProps<CustomerReviewProps & ProductAccordionPropsType>();
const viewport = useViewport();
const reviewsOpen = ref(!props.content.layout.initiallyCollapsed);
const route = useRoute();

const config = useRuntimeConfig().public;
const { currentProduct } = useProducts();

const product = computed(() => props.product || currentProduct.value);
const productId = computed(() => {
  const id = productGetters.getItemId(product.value);
  return id ? Number(id) : 0;
});
const productVariationId = computed(() => productGetters.getVariationId(product.value));

const {
  data: productReviews,
  authenticatedData: productAuthenticatedReviews,
  loading: loadingReviews,
  fetchReviews,
  fetchAuthenticatedReviews,
  reviewArea,
} = useProductReviews(productId.value, productVariationId.value);

watch([productId, productVariationId], async ([newId, newVariationId], [oldId, oldVariationId]) => {
  if ((newId !== oldId || newVariationId !== oldVariationId) && newId > 0) {
    await Promise.all([fetchReviews(), fetchAuthenticatedReviews()]);
  }
});

const paginatedProductReviews = computed(() => reviewGetters.getReviewItems(productReviews.value));
const authenticatedProductReviews = computed(() => reviewGetters.getReviewItems(productAuthenticatedReviews.value));
const pagination = computed(() => reviewGetters.getReviewPagination(productReviews.value));
const currentPage = computed(() => reviewGetters.getCurrentReviewsPage(productReviews.value));

const maxVisiblePages = computed(() => (viewport.isGreaterOrEquals('lg') ? 5 : 2));

const hasTitle = computed(() => {
  return props.content.text.title && props.content.text.title.trim().length > 0;
});

watch(
  () => reviewsOpen.value,
  async (value) => {
    if (value && hasTitle.value && props.content.layout.collapsible) {
      await Promise.all([fetchReviews(), fetchAuthenticatedReviews()]);
    }
  },
);
watch(
  () => route.query.feedbackPage,
  async () => {
    if (reviewArea.value) reviewArea.value.scrollIntoView({ behavior: 'smooth' });
  },
);

watch(
  () => route.query,
  () => fetchReviews(),
);

watch(
  () => props.content.layout.initiallyCollapsed,
  (newValue) => {
    reviewsOpen.value = !newValue;
  },
);

const customerReviewInlineStyle = computed(() => {
  const layout = props.content.layout ?? {};
  return {
    paddingTop: layout.paddingTop ? `${layout.paddingTop}px` : 0,
    paddingBottom: layout.paddingBottom ? `${layout.paddingBottom}px` : 0,
    paddingLeft: layout.paddingLeft ? `${layout.paddingLeft}px` : 0,
    paddingRight: layout.paddingRight ? `${layout.paddingRight}px` : 0,
  };
});
</script>
